/**
 * TierGate Component
 *
 * Conditionally renders children based on feature access.
 * Shows a fallback (typically an upgrade prompt) if feature is locked.
 */

import React from 'react';
import type { StartupFeatureKey, InvestorFeatureKey } from '@/lib/subscription/features';
import { useSubscriptionStore } from '@/lib/state/subscription-store';

// ============================================================================
// Types
// ============================================================================

interface TierGateProps {
  feature: StartupFeatureKey | InvestorFeatureKey;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

/**
 * TierGate - Conditionally render content based on feature access
 *
 * @example
 * ```tsx
 * <TierGate feature="direct_messaging" fallback={<UpgradePrompt lockedTitle="Direct Messaging" />}>
 *   <MessagingInterface />
 * </TierGate>
 * ```
 */
export function TierGate({ feature, children, fallback = null }: TierGateProps) {
  const hasFeature = useSubscriptionStore((s) => s.hasFeature(feature));

  if (hasFeature) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// ============================================================================
// Hook for Manual Checks
// ============================================================================

/**
 * Hook to check if current user has access to a feature
 *
 * @example
 * ```tsx
 * const canMessage = useCanAccessFeature('direct_messaging');
 * ```
 */
export function useCanAccessFeature(
  feature: StartupFeatureKey | InvestorFeatureKey
): boolean {
  return useSubscriptionStore((s) => s.hasFeature(feature));
}

/**
 * Hook to get the numeric limit for a feature
 *
 * @example
 * ```tsx
 * const teamSeats = useFeatureLimit('team_seats'); // Returns 1, 2, 4, 6
 * ```
 */
export function useFeatureLimit(
  feature: StartupFeatureKey | InvestorFeatureKey
): number {
  return useSubscriptionStore((s) => s.getFeatureLimit(feature));
}
